import {
    RecoveryDataError,
    hasValue,
    isAmount,
    selectSavedInsurance,
    selectSavedParking,
    toMinorUnits,
} from './recoveryData'

const callApi = async (callback, validationError) => {
    try {
        return await callback()
    } catch (error) {
        if (error instanceof RecoveryDataError) {
            throw error
        }

        throw new RecoveryDataError(
            error.status === 422
                ? validationError
                : 'recovery.requestFailed',
        )
    }
}

export const loadCheckoutRecovery = async (recovery, api) => {
    const offerResponse = await callApi(
        () => api.getOffer(recovery.request),
        'recovery.offerUnavailable',
    )

    const offer = offerResponse.offer

    if (!offer || offer.availibility !== 'r') {
        throw new RecoveryDataError('recovery.offerUnavailable')
    }

    if (
        !isAmount(offer.price) ||
        !offer.currency ||
        offer.currency.toUpperCase() !== recovery.currency
    ) {
        throw new RecoveryDataError('recovery.invalidOffer')
    }

    // Obecne podsumowanie, dodatki i saldo cashback są prezentowane w PLN.
    if (recovery.currency !== 'PLN') {
        throw new RecoveryDataError('recovery.unsupportedCurrency')
    }

    if (
        offer.departureDate !== recovery.request.od ||
        offer.returnDate !== recovery.request.do
    ) {
        throw new RecoveryDataError('recovery.offerChanged')
    }

    if (
        !Array.isArray(offerResponse.agreements) ||
        offerResponse.agreements.length === 0
    ) {
        throw new RecoveryDataError('recovery.agreementsUnavailable')
    }

    const form = {
        ...recovery.form,
        agreements: offerResponse.agreements.map((agreement) => ({
            ...agreement,
            checked: false,
        })),
    }

    const choices = recovery.choices
    const hasInsurance = hasValue(choices.insuranceVariant)
    const hasParking =
        hasValue(choices.parkingId) ||
        hasValue(choices.parkingOfferId)

    if (hasParking && !hasValue(choices.parkingAirportId)) {
        throw new RecoveryDataError('recovery.parkingUnavailable')
    }

    const request = {
        ...form,
        ...recovery.request,
        offer,
    }

    const [
        insuranceOffers,
        parkingAirports,
        parkingOffers,
        promotionPreview,
    ] = await Promise.all([
        hasInsurance
            ? callApi(
                () => api.getInsurances({
                    ...request,
                    step: 'INSURANCE',
                }),
                'recovery.insuranceUnavailable',
            )
            : Promise.resolve(null),

        hasParking
            ? callApi(
                () => api.getParkingAirports({
                    ...request,
                    step: 'PARKING',
                }),
                'recovery.parkingUnavailable',
            )
            : Promise.resolve([]),

        hasParking
            ? callApi(
                () => api.getParkingOffers({
                    ...request,
                    step: 'PARKING',
                    selectedAirportId: choices.parkingAirportId,
                }),
                'recovery.parkingUnavailable',
            )
            : Promise.resolve([]),

        choices.promotionCodes.length > 0
            ? callApi(
                () => api.calculatePromotions({
                    ...recovery.request,
                    promotionCodes: choices.promotionCodes,
                }),
                'recovery.promotionsUnavailable',
            )
            : Promise.resolve(null),
    ])

    const selectedInsurance = selectSavedInsurance(
        insuranceOffers,
        choices.insuranceVariant,
    )

    let selectedParkingOffer = null

    if (hasParking) {
        if (
            !Array.isArray(parkingAirports) ||
            !parkingAirports.some(
                (airport) =>
                    String(airport.id) === String(choices.parkingAirportId),
            )
        ) {
            throw new RecoveryDataError('recovery.parkingUnavailable')
        }

        selectedParkingOffer = selectSavedParking(parkingOffers, choices)
    }

    const currentOfferPrice = Number(offer.price) / 100

    if (
        promotionPreview &&
        (
            !isAmount(promotionPreview.tripAmountBefore) ||
            !isAmount(promotionPreview.tripAmountAfterGiftVouchers) ||
            toMinorUnits(promotionPreview.tripAmountBefore) !==
            Math.round(Number(offer.price))
        )
    ) {
        throw new RecoveryDataError('recovery.priceChangedAgain')
    }

    const userData = offerResponse.userData || {}

    if (choices.useCashbackForPay) {
        const currentEmail = String(userData.email || '')
            .trim()
            .toLowerCase()

        const savedEmail = String(form.email || '')
            .trim()
            .toLowerCase()

        if (
            !userData.id ||
            !savedEmail ||
            currentEmail !== savedEmail ||
            !isAmount(userData.cashbackBalance)
        ) {
            throw new RecoveryDataError('recovery.cashbackAccountRequired')
        }
    }

    return {
        request: {
            ...recovery.request,
        },
        offer,
        form,
        userData,
        dataUdds: offerResponse.dataUdds || recovery.dataUdds,
        adults: form.adults.length,
        children: form.children.length,
        insuranceOffers,
        selectedInsurance,
        parkingAirports,
        parkingOffers,
        selectedParkingAirport: hasParking
            ? choices.parkingAirportId
            : null,
        selectedParkingOffer,
        promotionCodes: [...choices.promotionCodes],
        promotionPreview,
        paymentMethod: choices.paymentMethod || undefined,
        useCashbackForPay: Boolean(choices.useCashbackForPay),
        earnedCashbackAmount: offerResponse.earnedCashbackAmount || 0,
        price: {
            original: recovery.originalOfferPrice,
            current: currentOfferPrice,
            currency: recovery.currency,
            changed:
                toMinorUnits(recovery.originalOfferPrice) !==
                Math.round(Number(offer.price)),
        },
    }
}
